/**
 * useReportingDashboard
 *
 * Derives all dashboard metrics from the reporting_entries cache in a single useMemo pass.
 * No API calls — everything is computed client-side from cached data.
 */

import { useMemo } from "react";
import { useReportingEntriesCache, useReportsCache, type ReportingEntry } from "@/shared/cache";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export interface DashboardStats {
  totalHours: number;
  exportedHours: number;
  trackedHours: number;
  entryCount: number;
  matterCount: number;
  avgPerDay: number;
}

export interface DailyChartPoint {
  date: string; // YYYY-MM-DD
  dateLabel: string; // "4/7"
  tooltipLabel: string; // "Monday 4/7"
  exported: number;
  tracked: number;
}

export interface MemberRow {
  profId: string;
  profName: string;
  profType: string | null;
  tracked: number;
  exported: number;
  total: number;
  entries: number;
  matters: number;
  avgPerDay: number;
}

export interface ClientRow {
  client: string;
  hours: number;
  exported: number;
  tracked: number;
  entries: number;
}

export interface MatterRow {
  matterId: string;
  matterNumber: string;
  matterDescription: string;
  client: string;
  hours: number;
  entries: number;
}

export interface DashboardFilters {
  dateRange: { from: Date; to: Date };
  /** null = all entries, "me" = user's own, "team" = direct reports only, prof_id = specific person */
  scope: string | null;
  userProfId: string;
  status: "all" | "tracked" | "exported";
  client: string | null;
  matter: string | null;
}

export interface DashboardData {
  stats: DashboardStats;
  dailyChart: DailyChartPoint[];
  memberTable: MemberRow[];
  topClients: ClientRow[];
  topMatters: MatterRow[];
  availableClients: string[];
  availableMatters: { matterId: string; matterNumber: string; matterDescription: string; client: string }[];
  isLoading: boolean;
}

// ─────────────────────────────────────────────
// Date Range Presets
// ─────────────────────────────────────────────

export type DatePreset =
  | "today"
  | "last-7"
  | "this-week"
  | "last-week"
  | "this-month"
  | "last-month"
  | "last-30"
  | "this-quarter"
  | "last-quarter"
  | "this-year";

export function getDateRange(preset: DatePreset): { from: Date; to: Date } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startOfWeek = (d: Date) => {
    const day = d.getDay();
    const diff = day === 0 ? 6 : day - 1; // Monday start
    const s = new Date(d);
    s.setDate(s.getDate() - diff);
    return s;
  };

  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "last-7": {
      const from = new Date(today);
      from.setDate(from.getDate() - 7);
      return { from, to: today };
    }
    case "this-week": {
      return { from: startOfWeek(today), to: today };
    }
    case "last-week": {
      const end = new Date(startOfWeek(today));
      end.setDate(end.getDate() - 1);
      return { from: startOfWeek(end), to: end };
    }
    case "this-month": {
      return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: today };
    }
    case "last-month": {
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const to = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from, to };
    }
    case "last-30": {
      const from = new Date(today);
      from.setDate(from.getDate() - 30);
      return { from, to: today };
    }
    case "this-quarter": {
      const q = Math.floor(today.getMonth() / 3);
      return { from: new Date(today.getFullYear(), q * 3, 1), to: today };
    }
    case "last-quarter": {
      const q = Math.floor(today.getMonth() / 3);
      const from = new Date(today.getFullYear(), (q - 1) * 3, 1);
      const to = new Date(today.getFullYear(), q * 3, 0);
      return { from, to };
    }
    case "this-year": {
      return { from: new Date(today.getFullYear(), 0, 1), to: today };
    }
  }
}

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  "today": "Today",
  "last-7": "Last 7 Days",
  "this-week": "This Week",
  "last-week": "Last Week",
  "this-month": "This Month",
  "last-month": "Last Month",
  "last-30": "Last 30 Days",
  "this-quarter": "This Quarter",
  "last-quarter": "Last Quarter",
  "this-year": "This Year",
};

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function parseEntryDate(dateStr: string): Date {
  const part = dateStr.split("T")[0];
  const [y, m, d] = part.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateLabel(dateKey: string): string {
  const [, m, d] = dateKey.split("-").map(Number);
  return `${m}/${d}`;
}

function formatTooltipLabel(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return `${days[date.getDay()]} ${m}/${d}`;
}

function isWeekday(d: Date): boolean {
  const day = d.getDay();
  return day !== 0 && day !== 6;
}

function countWeekdays(from: Date, to: Date): number {
  let count = 0;
  const d = new Date(from);
  while (d <= to) {
    if (isWeekday(d)) count++;
    d.setDate(d.getDate() + 1);
  }
  return count || 1; // avoid division by zero
}

// ─────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────

export function useReportingDashboard(filters: DashboardFilters): DashboardData {
  const { allEntries, userProfId: _userProfId, isLoading } = useReportingEntriesCache();
  const { reports } = useReportsCache();

  return useMemo(() => {
    // Collect available clients from ALL entries (unfiltered) so dropdown doesn't collapse
    const clientSet = new Set<string>();
    // Collect available matters scoped to selected client (or all if no client filter)
    const matterInfoMap = new Map<string, { matterId: string; matterNumber: string; matterDescription: string; client: string }>();
    for (const e of allEntries) {
      if (e.client) clientSet.add(e.client);
      if (e.matter_id && !matterInfoMap.has(e.matter_id)) {
        // Only include matters matching client filter (for the dropdown)
        if (!filters.client || e.client === filters.client) {
          matterInfoMap.set(e.matter_id, {
            matterId: e.matter_id,
            matterNumber: e.matter_number || "",
            matterDescription: e.matter_description || "",
            client: e.client || "Unknown",
          });
        }
      }
    }

    // Filter entries
    const filtered: ReportingEntry[] = [];
    for (const e of allEntries) {
      // Date filter
      const entryDate = parseEntryDate(e.date);
      if (entryDate < filters.dateRange.from || entryDate > filters.dateRange.to) continue;

      // Scope filter
      if (filters.scope === "me" && e.prof_id !== filters.userProfId) continue;
      if (filters.scope === "team" && e.prof_id === filters.userProfId) continue;
      if (filters.scope && filters.scope !== "me" && filters.scope !== "team" && e.prof_id !== filters.scope) continue;

      // Status filter
      if (filters.status === "exported" && !e.exported) continue;
      if (filters.status === "tracked" && e.exported) continue;

      // Client filter
      if (filters.client && e.client !== filters.client) continue;

      // Matter filter
      if (filters.matter && e.matter_id !== filters.matter) continue;

      filtered.push(e);
    }

    // Single-pass aggregation
    let totalHours = 0;
    let exportedHours = 0;
    let trackedHours = 0;
    const matterIds = new Set<string>();
    const dailyMap = new Map<string, { exported: number; tracked: number }>();
    const memberMap = new Map<string, {
      profName: string; profType: string | null;
      tracked: number; exported: number; entries: number;
      matters: Set<string>; days: Set<string>;
    }>();
    const clientMap = new Map<string, { hours: number; exported: number; tracked: number; entries: number }>();
    const matterMap = new Map<string, {
      matterNumber: string; matterDescription: string; client: string;
      hours: number; entries: number;
    }>();

    for (const e of filtered) {
      const units = e.units;
      totalHours += units;
      if (e.exported) exportedHours += units;
      else trackedHours += units;

      if (e.matter_id) matterIds.add(e.matter_id);

      // Daily chart
      const dateKey = toDateKey(parseEntryDate(e.date));
      const daily = dailyMap.get(dateKey) || { exported: 0, tracked: 0 };
      if (e.exported) daily.exported += units;
      else daily.tracked += units;
      dailyMap.set(dateKey, daily);

      // Member table
      let member = memberMap.get(e.prof_id);
      if (!member) {
        const person = reports.manages.find((p) => p.prof_id === e.prof_id);
        member = {
          profName: e.prof_name || person?.full_name || e.prof_id,
          profType: person?.prof_type || null,
          tracked: 0, exported: 0, entries: 0,
          matters: new Set(), days: new Set(),
        };
        memberMap.set(e.prof_id, member);
      }
      if (e.exported) member.exported += units;
      else member.tracked += units;
      member.entries++;
      if (e.matter_id) member.matters.add(e.matter_id);
      member.days.add(dateKey);

      // Client breakdown
      const clientName = e.client || "Unknown";
      let cl = clientMap.get(clientName);
      if (!cl) { cl = { hours: 0, exported: 0, tracked: 0, entries: 0 }; clientMap.set(clientName, cl); }
      cl.hours += units;
      if (e.exported) cl.exported += units;
      else cl.tracked += units;
      cl.entries++;

      // Matter breakdown
      if (e.matter_id) {
        let mt = matterMap.get(e.matter_id);
        if (!mt) {
          mt = {
            matterNumber: e.matter_number || "",
            matterDescription: e.matter_description || "",
            client: e.client || "Unknown",
            hours: 0, entries: 0,
          };
          matterMap.set(e.matter_id, mt);
        }
        mt.hours += units;
        mt.entries++;
      }
    }

    // Build outputs
    const weekdays = countWeekdays(filters.dateRange.from, filters.dateRange.to);

    const stats: DashboardStats = {
      totalHours: Math.round(totalHours * 10) / 10,
      exportedHours: Math.round(exportedHours * 10) / 10,
      trackedHours: Math.round(trackedHours * 10) / 10,
      entryCount: filtered.length,
      matterCount: matterIds.size,
      avgPerDay: Math.round((totalHours / weekdays) * 10) / 10,
    };

    // Daily chart: fill in missing dates in range
    const dailyChart: DailyChartPoint[] = [];
    const d = new Date(filters.dateRange.from);
    while (d <= filters.dateRange.to) {
      const key = toDateKey(d);
      const val = dailyMap.get(key) || { exported: 0, tracked: 0 };
      dailyChart.push({
        date: key,
        dateLabel: formatDateLabel(key),
        tooltipLabel: formatTooltipLabel(key),
        exported: Math.round(val.exported * 10) / 10,
        tracked: Math.round(val.tracked * 10) / 10,
      });
      d.setDate(d.getDate() + 1);
    }

    const memberTable: MemberRow[] = Array.from(memberMap.entries())
      .map(([profId, m]) => ({
        profId,
        profName: m.profName,
        profType: m.profType,
        tracked: Math.round(m.tracked * 10) / 10,
        exported: Math.round(m.exported * 10) / 10,
        total: Math.round((m.tracked + m.exported) * 10) / 10,
        entries: m.entries,
        matters: m.matters.size,
        avgPerDay: Math.round(((m.tracked + m.exported) / (m.days.size || 1)) * 10) / 10,
      }))
      .sort((a, b) => b.total - a.total);

    const topClients: ClientRow[] = Array.from(clientMap.entries())
      .map(([client, c]) => ({
        client,
        hours: Math.round(c.hours * 10) / 10,
        exported: Math.round(c.exported * 10) / 10,
        tracked: Math.round(c.tracked * 10) / 10,
        entries: c.entries,
      }))
      .sort((a, b) => b.hours - a.hours);

    const topMatters: MatterRow[] = Array.from(matterMap.entries())
      .map(([matterId, m]) => ({
        matterId,
        matterNumber: m.matterNumber,
        matterDescription: m.matterDescription,
        client: m.client,
        hours: Math.round(m.hours * 10) / 10,
        entries: m.entries,
      }))
      .sort((a, b) => b.hours - a.hours);

    return {
      stats,
      dailyChart,
      memberTable,
      topClients,
      topMatters,
      availableClients: Array.from(clientSet).sort(),
      availableMatters: Array.from(matterInfoMap.values()).sort((a, b) => a.matterNumber.localeCompare(b.matterNumber)),
      isLoading,
    };
  }, [allEntries, reports, filters, isLoading]);
}
